import type { Placement } from '@floating-ui/react';
import { decodeEntities } from '@wordpress/html-entities';

/**
 * Floating Tooltip component.
 * Displays location info anchored to the mouse position via Floating UI.
 *
 * @param {Object}      props               - component props
 * @param {MapLocation} props.location      - location data to display.
 * @param {Object}      props.floatingStyles - Floating UI positioning styles.
 * @param {Function}    props.refCallback   - ref callback from Floating UI.
 * @param {Function}    props.getFloatingProps - Floating UI interaction props.
 * @param {boolean}     props.isPositioned  - whether Floating UI has calculated position.
 * @param {Placement}   props.placement     - resolved Floating UI placement.
 * @param {string}      props.ctaText       - call-to-action label (pass via localized data for i18n).
 * @return {*} jsx
 */
export default function FloatingTooltip( {
	location,
	floatingStyles,
	refCallback,
	getFloatingProps,
	isPositioned,
	placement,
	ctaText = 'Click dot to learn more',
}: {
	location: MapLocation;
	floatingStyles: Record< string, string | number | undefined >;
	refCallback: ( element: HTMLElement | null ) => void;
	getFloatingProps: () => Record< string, unknown >;
	isPositioned: boolean;
	placement: Placement;
	ctaText?: string;
} ) {
	const { title, city, state, color, location_type } = location;

	return (
		<div
			id="str-maps-tooltip"
			role="tooltip"
			ref={ refCallback }
			data-placement={ placement }
			className={ `str-maps__tooltip ${ isPositioned ? 'str-maps__tooltip--is-positioned' : '' } ${ color ? 'has-custom-color' : '' }` }
			style={ {
				...floatingStyles,
				pointerEvents: 'none',
				...( color ? { '--str-maps-location-color': color } : {} ),
			} as Record< string, string | number | undefined > }
			{ ...getFloatingProps() }
		>
			<p className="str-maps__tooltip__title">
				{ decodeEntities( title ) }
			</p>
			<p className="str-maps__tooltip__location tag-text">
				{ city?.length ? `${city},` : '' } { state ?? '' }
			</p>
			{ location_type && (
				<p className="str-maps__tooltip__type">
					{ decodeEntities( location_type ) }
				</p>
			) }
			<p className="str-maps__tooltip__cta">
				{ ctaText }
			</p>
		</div>
	);
}
